import { useEffect } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { Button, SocialLoginButton } from '@silvertours/front-shared';
import { analytics, socialLogin } from '@silvertours/front-entities';
import AccountIllustration from './account_illustration.png';
import { LoginError } from './LoginError';
import { useLogin } from './LoginState';
import { AccountUsps } from './AccountUsps';
import {
  IllustrationWrapper,
  Options,
  OptionsHeadline,
  Providers,
} from './SocialOptions.styles';

/* Written out for each provider, not built from the key. A search for a
   translation key must find it in the translation files. */
const PROVIDER_LABELS = {
  apple: 'social.options.apple',
  google: 'social.options.google',
} as const;

type Props = {
  /** Switches to the email and password step. */
  onEmailClick: () => void;
};

/**
 * The provider step. It shows the two social buttons, with "Continue with
 * Email" below them, in front of the email form.
 */
const SocialOptions = ({ onEmailClick }: Props) => {
  const t = useTranslations('features.navigationLegacy.login');
  const {
    error,
    availableProviders,
    isSocialLoginProcessing,
    pendingProvider,
    startSocialLogin,
  } = useLogin();

  useEffect(() => {
    // One impression for each provider button. This gives each button its own
    // click-through rate.
    socialLogin.SOCIAL_LOGIN_PROVIDERS.forEach(provider => {
      analytics.generic.trackSocialLoginButtonsViewed({
        placement: 'login_gate',
        login_provider: provider,
      });
    });
  }, []);

  return (
    <Options>
      {/* The banner replaces the illustration, so the step
          keeps its height when an attempt fails. */}
      {!error && (
        <IllustrationWrapper>
          <Image src={AccountIllustration} alt="" unoptimized />
        </IllustrationWrapper>
      )}

      <LoginError />

      <OptionsHeadline>{t('subTitle')}</OptionsHeadline>

      <Providers>
        {socialLogin.SOCIAL_LOGIN_PROVIDERS.map(provider => (
          <SocialLoginButton
            key={provider}
            provider={provider}
            /*
             * Every button locks while an attempt runs, the running one
             * included. `start` does not refuse a second call, so a second
             * click would open another provider window and exchange again.
             */
            disabled={!availableProviders[provider] || isSocialLoginProcessing}
            loading={pendingProvider === provider}
            onClick={() => startSocialLogin(provider)}
            data-testid={`${provider}Login`}
          >
            {t(PROVIDER_LABELS[provider])}
          </SocialLoginButton>
        ))}
        <Button
          fullWidth
          disabled={isSocialLoginProcessing}
          onClick={() => {
            analytics.generic.trackEmailLoginChosen({
              placement: 'login_gate',
            });
            onEmailClick();
          }}
          data-testid="emailLogin"
        >
          {t('social.options.email')}
        </Button>
      </Providers>

      <AccountUsps />
    </Options>
  );
};

export { SocialOptions };
